import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Star, Trash2, Pencil, Camera, Eye, EyeOff, RefreshCw, Search, Plus, X, Check, Phone, MessageSquare } from "lucide-react";
import { api } from "../lib/api";

// Сжатие изображения до dataURL (макс. сторона maxSize)
const resizeImage = (file, maxSize = 1200, quality = 0.85) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      let { width, height } = img;
      if (width > maxSize || height > maxSize) {
        const k = Math.min(maxSize / width, maxSize / height);
        width = Math.round(width * k);
        height = Math.round(height * k);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    img.src = url;
  });

function Stars({ value, onChange, size = 15 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <button key={i} type="button" disabled={!onChange} onClick={() => onChange && onChange(i)}
          className={onChange ? "transition-transform active:scale-90" : "cursor-default"}>
          <Star size={size} className={i <= (value || 0) ? "fill-[#FEBC10] text-[#FEBC10]" : "text-gray-300"} />
        </button>
      ))}
    </div>
  );
}

const EMPTY_NEW = { name: "", phone: "", rating: 5, text: "" };

const StatCard = ({ value, label, testid }) => (
  <div className="bg-white rounded-2xl p-4 border border-gray-100 text-center" data-testid={testid}>
    <div className="font-display font-black text-2xl">{value}</div>
    <div className="text-xs text-gray-400 font-bold uppercase mt-0.5">{label}</div>
  </div>
);

export default function Reviews() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [ratingFilter, setRatingFilter] = useState(0);
  const [edit, setEdit] = useState(null); // {id, name, text, rating}
  const [creating, setCreating] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const photoTargetId = useRef(null);

  const load = () =>
    api.get("/site-reviews", { params: { include_hidden: true } })
      .then((r) => { setItems(r.data.reviews || []); setStats(r.data.stats || null); })
      .catch(() => toast.error("Не удалось загрузить отзывы"));
  useEffect(() => { load(); }, []);

  const hiddenCount = useMemo(() => items.filter((r) => r.hidden).length, [items]);
  const filtered = useMemo(() => {
    let list = items;
    if (ratingFilter > 0) list = list.filter((r) => r.rating === ratingFilter);
    const s = q.trim().toLowerCase();
    if (s) list = list.filter((r) => (r.name || "").toLowerCase().includes(s) || (r.text || "").toLowerCase().includes(s) || (r.phone || "").toLowerCase().includes(s));
    return list;
  }, [items, ratingFilter, q]);

  const put = async (body, okMsg) => {
    setBusy(true);
    try {
      const { data } = await api.put("/site-reviews", body);
      if (!data.success) throw new Error(data.error || "error");
      if (okMsg) toast.success(okMsg);
      await load();
    } catch (e) { toast.error("Ошибка сохранения"); }
    setBusy(false);
  };

  const toggleHidden = (r) => put({ id: r.id, hidden: !r.hidden }, r.hidden ? "Отзыв снова показывается на сайте" : "Отзыв скрыт с сайта");

  const saveEdit = async () => {
    if (!edit.text.trim()) { toast.error("Текст отзыва не может быть пустым"); return; }
    if (!edit.name.trim()) { toast.error("Имя не может быть пустым"); return; }
    await put({ id: edit.id, text: edit.text.trim(), name: edit.name.trim(), rating: edit.rating }, "Отзыв обновлён");
    setEdit(null);
  };

  const del = async (r) => {
    if (!window.confirm(`Удалить отзыв от «${r.name}»? Это действие необратимо.`)) return;
    try {
      await api.delete("/site-reviews", { data: { id: r.id } });
      toast.success("Отзыв удалён");
      load();
    } catch (e) { toast.error("Ошибка удаления"); }
  };

  const pickPhoto = (r) => { photoTargetId.current = r.id; fileRef.current?.click(); };
  const onPhotoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !photoTargetId.current) return;
    try {
      const dataUrl = await resizeImage(file);
      await put({ id: photoTargetId.current, photo: dataUrl }, "Фото сохранено");
    } catch (err) { toast.error("Не удалось обработать фото"); }
    photoTargetId.current = null;
  };
  const removePhoto = (r) => { if (window.confirm("Убрать фото у этого отзыва?")) put({ id: r.id, photo: null }, "Фото удалено"); };

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/site-reviews", { ...creating, name: creating.name.trim(), text: creating.text.trim() });
      if (!data.success) throw new Error(data.error || "error");
      toast.success("Отзыв добавлен");
      setCreating(null);
      load();
    } catch (e) { toast.error("Ошибка добавления"); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="admin-reviews">
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhotoFile} />
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h1 className="font-display font-black text-2xl">Отзывы сайта</h1>
        <div className="flex gap-2">
          <button className="admin-btn admin-btn-ghost" onClick={load} data-testid="reviews-refresh-btn"><RefreshCw size={15} /> Обновить</button>
          <button className="admin-btn" onClick={() => setCreating({ ...EMPTY_NEW })} data-testid="reviews-create-btn"><Plus size={16} /> Отзыв</button>
        </div>
      </div>

      {/* Статистика — как на сайте (по видимым отзывам) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard value={stats?.total ?? "—"} label="Всего отзывов" testid="reviews-stat-total" />
        <StatCard value={stats?.average != null ? Number(stats.average).toFixed(1) : "—"} label="Средняя оценка" testid="reviews-stat-avg" />
        <StatCard value={stats?.five_stars ?? "—"} label="5 звёзд" testid="reviews-stat-five" />
        <StatCard value={hiddenCount} label="Скрыто с сайта" testid="reviews-stat-hidden" />
      </div>

      {/* Форма добавления */}
      {creating && (
        <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-2.5" data-testid="review-create-form">
          <div className="font-bold text-sm">Новый отзыв</div>
          <div className="grid md:grid-cols-2 gap-2">
            <input className="admin-input" placeholder="Имя гостя" value={creating.name} onChange={(e) => setCreating({ ...creating, name: e.target.value })} data-testid="review-new-name" />
            <input className="admin-input" placeholder="Телефон (необязательно)" value={creating.phone} onChange={(e) => setCreating({ ...creating, phone: e.target.value })} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-gray-500">Оценка:</span>
            <Stars value={creating.rating} onChange={(v) => setCreating({ ...creating, rating: v })} size={22} />
          </div>
          <textarea className="admin-input min-h-[90px]" placeholder="Текст отзыва" value={creating.text} onChange={(e) => setCreating({ ...creating, text: e.target.value })} data-testid="review-new-text" />
          <div className="flex gap-2 justify-end">
            <button className="admin-btn admin-btn-ghost" onClick={() => setCreating(null)}>Отмена</button>
            <button className="admin-btn" disabled={busy || !creating.name.trim() || !creating.text.trim()} onClick={create} data-testid="review-new-save">Добавить</button>
          </div>
        </div>
      )}

      {/* Поиск и фильтр по оценке */}
      <div className="bg-white rounded-2xl p-3.5 border border-gray-100 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="admin-input pl-9" placeholder="Поиск по имени, тексту или телефону..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="reviews-search-input" />
        </div>
        <div className="flex gap-1.5">
          <button onClick={() => setRatingFilter(0)} data-testid="reviews-filter-all"
            className={`px-3 py-1.5 rounded-full text-xs font-bold ${ratingFilter === 0 ? "bg-[#FEBC10] text-black" : "bg-gray-100 text-gray-500"}`}>Все</button>
          {[5, 4, 3, 2, 1].map((n) => (
            <button key={n} onClick={() => setRatingFilter(n)} data-testid={`reviews-filter-${n}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1 ${ratingFilter === n ? "bg-[#FEBC10] text-black" : "bg-gray-100 text-gray-500"}`}>
              {n} <Star size={11} className={ratingFilter === n ? "fill-black text-black" : "fill-gray-400 text-gray-400"} />
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-400 font-bold ml-auto" data-testid="reviews-count">{filtered.length} / {items.length}</span>
      </div>

      {/* Список отзывов */}
      <div className="grid md:grid-cols-2 gap-3">
        {filtered.length === 0 && (
          <div className="col-span-full bg-white rounded-2xl p-8 border border-gray-100 text-center text-gray-400 font-bold text-sm">
            <MessageSquare size={28} className="mx-auto mb-2 text-gray-300" /> Отзывы не найдены
          </div>
        )}
        {filtered.map((r) => {
          const isEdit = edit?.id === r.id;
          return (
            <div key={r.id} data-testid={`review-card-${r.id}`}
              className={`bg-white rounded-2xl border p-4 space-y-2.5 ${r.hidden ? "border-dashed border-gray-300 opacity-70" : "border-gray-100"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  {isEdit ? (
                    <input className="admin-input" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid={`review-edit-name-${r.id}`} />
                  ) : (
                    <div className="font-bold text-sm truncate flex items-center gap-2">
                      {r.name}
                      {r.hidden && <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 text-[10px] font-black uppercase"><EyeOff size={10} /> Скрыт</span>}
                    </div>
                  )}
                  {r.phone && !isEdit && (
                    <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1 text-xs font-bold text-[#FF0000] mt-0.5"><Phone size={11} /> {r.phone}</a>
                  )}
                </div>
                <div className="shrink-0">
                  {isEdit
                    ? <Stars value={edit.rating} onChange={(v) => setEdit({ ...edit, rating: v })} size={18} />
                    : <Stars value={r.rating} />}
                </div>
              </div>

              {isEdit ? (
                <textarea className="admin-input min-h-[90px]" value={edit.text} onChange={(e) => setEdit({ ...edit, text: e.target.value })} data-testid={`review-edit-text-${r.id}`} />
              ) : (
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{r.text}</p>
              )}

              {r.photo && !isEdit && <img src={r.photo} alt="" className="h-24 rounded-xl object-cover" />}

              <div className="flex flex-wrap items-center gap-1.5 pt-1">
                {isEdit ? (
                  <>
                    <button className="admin-btn" disabled={busy} onClick={saveEdit} data-testid={`review-save-${r.id}`}><Check size={14} /> Сохранить</button>
                    <button className="admin-btn admin-btn-ghost" onClick={() => setEdit(null)}><X size={14} /> Отмена</button>
                  </>
                ) : (
                  <>
                    <button title="Редактировать" data-testid={`review-edit-${r.id}`}
                      onClick={() => setEdit({ id: r.id, name: r.name || "", text: r.text || "", rating: r.rating || 5 })}
                      className="p-2 bg-yellow-50 text-[#92600a] rounded-lg hover:bg-yellow-100"><Pencil size={14} /></button>
                    <button title={r.hidden ? "Показать на сайте" : "Скрыть с сайта"} data-testid={`review-hide-${r.id}`} disabled={busy}
                      onClick={() => toggleHidden(r)}
                      className={`p-2 rounded-lg ${r.hidden ? "bg-green-50 text-green-600 hover:bg-green-100" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>
                      {r.hidden ? <Eye size={14} /> : <EyeOff size={14} />}
                    </button>
                    <button title={r.photo ? "Заменить фото" : "Добавить фото"} data-testid={`review-photo-${r.id}`}
                      onClick={() => pickPhoto(r)} className="p-2 bg-gray-100 text-gray-500 rounded-lg hover:bg-gray-200"><Camera size={14} /></button>
                    {r.photo && (
                      <button title="Убрать фото" onClick={() => removePhoto(r)} className="p-2 bg-gray-100 text-gray-500 rounded-lg hover:bg-gray-200"><X size={14} /></button>
                    )}
                    <button title="Удалить отзыв" data-testid={`review-delete-${r.id}`}
                      onClick={() => del(r)} className="p-2 bg-gray-100 text-red-500 rounded-lg hover:bg-red-50 ml-auto"><Trash2 size={14} /></button>
                  </>
                )}
                <span className="text-[11px] text-gray-400 font-semibold w-full">{r.date}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
